import type { NodeBaseModel } from '../models';
import { InstanceUtils } from '../misc';
import { contractInputGroups, contractInputReference, contractReferenceKey } from '../history/ContractDraftScope';

// Tracks the lifetime of the form graph, independently of history attribution.
export default class ContractSourceRequests {
    generation = 0;

    private pending = new Set<Promise<unknown>>();
    private invalidateSettlement!: () => void;
    private generationChanged = this.nextGenerationSignal();

    constructor(private readonly nodes: () => NodeBaseModel[]) {}

    track<T>(task: Promise<T>): Promise<T> {
        this.pending.add(task);
        void task.then(
            () => this.pending.delete(task),
            () => this.pending.delete(task)
        );
        return task;
    }

    async settle() {
        do {
            await Promise.race([Promise.all(Array.from(this.pending)), this.generationChanged]);
            await Promise.resolve();
        } while (this.pending.size);
    }

    clear() {
        ++this.generation;
        this.pending.clear();
        // Discarded requests may never complete. Waiters follow replacement work.
        this.invalidateSettlement();
        this.generationChanged = this.nextGenerationSignal();
    }

    replaceGraph(rebuild: () => void) {
        const refreshes = new Map(
            this.inputs().map(input => [
                contractReferenceKey(contractInputReference(input)),
                input.suspendSourceRefresh()
            ])
        );
        this.clear();
        rebuild();
        this.inputs().forEach(input => {
            refreshes.get(contractReferenceKey(contractInputReference(input)))?.(input);
        });
    }

    private inputs() {
        return contractInputGroups(this.nodes())
            .flatMap(group => group.inputs)
            .filter(InstanceUtils.isInputWithSource);
    }

    private nextGenerationSignal() {
        return new Promise<void>(resolve => {
            this.invalidateSettlement = resolve;
        });
    }
}
